/**
 * The real Ask provider: forwards questions to our own API and relays its stream.
 *
 * This is a thin transport, not the assistant. It holds no model, no key, and no opinion
 * about what makes a good answer — it POSTs `{ question, context }` to
 * `POST /ask/admin/answer/stream` and re-yields exactly the `AskStreamEvent`s the server
 * sends, in the order they arrive. The answering CONTRACT — ground `context` faithfully,
 * never fabricate when neither the context nor a tool covers the question (emit
 * `no_docs`/`no_data` instead — VRG-227's split, see ./types.ts — and let the UI, not the
 * model, decide whether to open a support request), exactly three read-only tools,
 * company scope applied server-side and never an argument the model can set — is
 * enforced in apps/api/src/features/ask/ask.service.ts.
 * This file has no way to check any of that and would relay a violation of the contract
 * exactly as faithfully as it relays a good answer. Correctness lives on the server; this
 * file's only job is to not lose or misread a byte of what it sends.
 *
 * `configured: true` is optimistic, not a guarantee. The client cannot know ahead of a
 * call whether the server has a model wired up (Vertex credentials, etc.) — checking
 * first would mean a second round trip before every question, or caching an answer that
 * can go stale mid-session if the server's config changes underneath it. So this always
 * offers to send, and treats the server as the one source of truth: when nothing is
 * configured server-side, the stream's first (and only) event is
 * `{ type: 'error', message: ... }` followed by `done` — which the runner turns into a
 * real, visible `status: 'error'` message. Never a fabricated answer standing in for
 * "unconfigured".
 */
import { API_BASE_URL, storage } from 'utils'
import { renewPlatformSession } from 'services/platformSession'
import { permissionLabel } from 'utils/permissionLabels'
import { ASK_PERMISSION, describeAskForbidden } from 'utils/veraSupportAccess'

import type { AskProvider, AskRequest } from './provider'
import { readSseEvents } from './sse'
import type { AskStreamEvent } from './types'

const ANSWER_STREAM_PATH = '/ask/admin/answer/stream'

/**
 * Auth + company-scope headers, replicated by hand from `services/index.ts`'s axios
 * instance.
 *
 * This call is made with a bare `fetch`, not through that axios instance — SSE streaming
 * needs `response.body.getReader()`, and axios has no supported way to hand back a raw
 * stream reader instead of buffering the whole response. So the headers that make a
 * request actually authenticate have to be rebuilt here.
 *
 * We deliberately do NOT send `credentials: 'include'`. The API answers with
 * `Access-Control-Allow-Origin: *`, and the CORS spec makes a wildcard origin illegal on
 * any credentialed request — so the browser lets the OPTIONS preflight through (204) and
 * then blocks the actual POST as `net::ERR_FAILED`, which surfaces to the user as
 * "Couldn't reach Vera" with no server-side trace, because the request DOES reach Nest
 * and only the response is rejected. Verified against the running API on 2026-08-26.
 * Nothing is lost by omitting it: the cookie it would carry is not what authenticates
 * this call anyway, per the note below.
 *
 * `credentials: 'include'` alone is NOT enough to authenticate this request: the API's
 * `AuthGuard.extractToken` (apps/api/src/infrastructure/guards/auth.guard.ts) checks a
 * `vera_auth_token` cookie first, then falls back to an `Authorization: Bearer` header,
 * then `X-Vera-Token` — but this app never sets that cookie. It stores the session token
 * itself (`accessToken`, via js-cookie with a localStorage fallback — see utils/storage.ts)
 * and sends it as a header on every request, which is what `X-Vera-Token` below
 * reproduces. `X-Vera-Company` / `X-Vera-Space` carry the same company-scoping every
 * other authenticated call in this app already relies on; Ask's `@RequireAdmin()` route
 * resolves `@CompanyId()` from the same place every other admin route does.
 */
function buildRequestHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }

  const token = storage.getItem('accessToken')
  if (token) headers['X-Vera-Token'] = `${token}`

  const company = storage.getItem('selectedCompany')
  if (company) headers['X-Vera-Company'] = `${company}`

  // VERA-876: when Vera staff are operating inside a company's space, the effective
  // company scope is the space company, not `selectedCompany` — mirrors
  // services/index.ts's createApiCall exactly, including which header wins.
  const spaceCompanyId = readSpaceCompanyId()
  if (spaceCompanyId) {
    headers['X-Vera-Space'] = spaceCompanyId
    headers['X-Vera-Company'] = spaceCompanyId
  }

  return headers
}

/** The space company from the persisted `veraSpace` store, or null outside a space. */
function readSpaceCompanyId(): string | null {
  try {
    const rawSpace = localStorage.getItem('veraSpace')
    if (!rawSpace) return null
    return (JSON.parse(rawSpace) as {state?: {companyId?: string}})?.state?.companyId || null
  } catch {
    // Malformed/absent veraSpace — treated as not in a space.
    return null
  }
}

/** The server's own message from an error body, if it sent one. */
async function readServerMessage(response: Response): Promise<string | null> {
  try {
    const text = await response.text()
    if (!text) return null
    try {
      const parsed = JSON.parse(text) as {message?: string | string[]}
      if (typeof parsed.message === 'string') return parsed.message
      if (Array.isArray(parsed.message)) return parsed.message.join(', ')
    } catch {
      // Not JSON — fall through to the raw text.
    }
    return text
  } catch {
    // Body already consumed, or unreadable.
    return null
  }
}

/** Best-effort human-readable message for a non-OK, or non-stream, HTTP response. */
async function describeFailedResponse(response: Response): Promise<string> {
  const serverMessage = await readServerMessage(response)

  // VRG-677: a permission 403 becomes the sentence the panel shows up front (whose
  // permission is missing and who can grant it), instead of a bare "Insufficient
  // permissions". Other 403s on this route keep the server's message; see
  // describeAskForbidden for why.
  const forbidden = describeAskForbidden({
    status: response.status,
    serverMessage,
    inSpace: !!readSpaceCompanyId(),
    permissionLabel: permissionLabel(ASK_PERMISSION),
  })
  if (forbidden) return forbidden

  return (
    serverMessage ??
    `Vera answered with an error (${response.status}${response.statusText ? ` ${response.statusText}` : ''}).`
  )
}

const isAbortError = (err: unknown): boolean =>
  (err instanceof DOMException && err.name === 'AbortError') ||
  (err as {name?: string} | undefined)?.name === 'AbortError'

/**
 * `fetch()` rejecting (as opposed to resolving with a bad status) means the request
 * never reached a server at all — connection refused, DNS failure, offline, or CORS
 * blocking the actual request. The browser's own message for this ("Failed to fetch" in
 * Chrome, "NetworkError when attempting to fetch resource" in Firefox, "Load failed" in
 * Safari) is internal engine wording, not something a brand operator can act on, and it
 * is genuinely the SAME string for every one of those causes — so this message says only
 * what is actually known (we could not reach the server) and asks for the one thing a
 * user can check themselves, rather than guessing at which of those causes applied.
 */
const UNREACHABLE_MESSAGE = "Couldn't reach Vera to answer that. Check your connection and try again."

/**
 * A read failure AFTER the connection was established and the stream had started is a
 * different, narrower fact than never connecting at all — the request reached the
 * server, headers came back, and the connection then dropped (or the server ended it)
 * partway through. Worth its own message: distinguishing "never got there" from "got
 * partway there" is the honest amount of detail this layer can support.
 */
const STREAM_INTERRUPTED_MESSAGE =
  'Lost the connection to Vera partway through the answer. Try asking again.'

export const httpAskProvider: AskProvider = {
  id: 'vera-api',
  configured: true,
  async *stream(request: AskRequest): AsyncIterable<AskStreamEvent> {
    const {question, context, signal} = request

    let response: Response
    try {
      const send = () =>
        fetch(`${API_BASE_URL}${ANSWER_STREAM_PATH}`, {
          method: 'POST',
          headers: buildRequestHeaders(),
          body: JSON.stringify({question, context}),
          signal,
        })
      response = await send()
      // This is a bare fetch, so it does not get services/index.ts's 401 handling. The access
      // token lives ~15 minutes; renew once from the session cookie and retry, exactly as the
      // axios client does, rather than show "not authorised" to someone still signed in.
      if (
        response.status === 401 &&
        (await renewPlatformSession(storage.getItem('accessToken'))).status === 'renewed'
      ) {
        response = await send()
      }
    } catch (err) {
      // Abort during the fetch itself (e.g. the user hit stop before headers came back) —
      // exit cleanly, no error event. The runner's own abort handling settles the message.
      if (signal.aborted || isAbortError(err)) return
      // A rejected fetch() means the request never reached a server — see
      // UNREACHABLE_MESSAGE's comment for why the raw browser error isn't shown. Keep it
      // in the console so this stays debuggable without exposing it to the user.
      console.error('[ask] Could not reach the answer endpoint.', err)
      yield {type: 'error', message: UNREACHABLE_MESSAGE}
      yield {type: 'done'}
      return
    }

    if (!response.ok || !response.body) {
      yield {type: 'error', message: await describeFailedResponse(response)}
      yield {type: 'done'}
      return
    }

    const reader = response.body.getReader()
    try {
      for await (const payload of readSseEvents(reader)) {
        if (signal.aborted) return

        let event: AskStreamEvent
        try {
          event = JSON.parse(payload) as AskStreamEvent
        } catch {
          // Malformed line on the wire — skip it rather than crash the turn. The
          // stream's own `done` event (or its absence, on a hard disconnect) is what
          // ultimately settles the message.
          continue
        }
        yield event
      }
    } catch (err) {
      if (signal.aborted || isAbortError(err)) return
      console.error('[ask] The answer stream was interrupted while reading.', err)
      yield {type: 'error', message: STREAM_INTERRUPTED_MESSAGE}
      yield {type: 'done'}
    } finally {
      try {
        reader.releaseLock()
      } catch {
        // Already released, or the stream errored out from under it — either way there
        // is nothing left to clean up.
      }
    }
  },
}
